import { colors } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { TrendsOverTimeChart } from "../../../components/TrendOverTimeChart/TrendsOverTimeChart.component";
import { MetricConfig } from "../../../components/TrendOverTimeChart/TrendsOverTimeChart.types";
import { ChallengeEventType } from "../../../directus/collections";
import { MatchFilter } from "../../../libs/statsFilters";
import { WorkerQueryTransferModes } from "../../../libs/workers/consts";
import { WorkerQueryOptions } from "../../../libs/workers/types";
import { webWorkerQuery } from "../../../libs/workers/webWorker";
import EnigmaTrendsOverTimeChartWorker from "./EnigmaTrendsOverTimeChart.worker.ts?worker";

interface EnigmaMetricsChartProps {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
  loading?: boolean;
}

export type EnigmaMetricConfig = MetricConfig & { key: ChallengeEventType };

const ENIGMA_METRICS: EnigmaMetricConfig[] = [
  {
    key: "challenge_init",
    label: "Avvii",
    color: colors.blue[200],
    enabled: true,
  },
  {
    key: "challenge_solution",
    label: "Successi",
    color: colors.green[200],
    enabled: true,
  },
  {
    key: "challenge_hint",
    label: "Ispirazione chiesta",
    color: colors.yellow[200],
    enabled: true,
  },
  {
    key: "challenge_requested_solution",
    label: "Rese",
    color: colors.red[200],
    enabled: true,
  },
];

export function EnigmaTrendsOverTimeChart({
  cityEscapeId,
  enigmaId,
  filters,
  loading = false,
}: EnigmaMetricsChartProps) {
  const timelines = useEnigmaTrendsOverTimeChartTimelines(
    cityEscapeId,
    enigmaId,
    filters,
    {
      response: {
        transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        skipDecoding: true,
      },
    }
  );

  const handleAreaClick = (event: any, data: any) => {
    console.log("Area click", event, data);
  };

  const handleAxisClick = (event: any, data: any) => {
    console.log("X Axis click", event, data);
  };

  return (
    <TrendsOverTimeChart
      version="mono"
      title="Metriche Enigma nel Tempo"
      timelineData={timelines.data ?? []}
      metrics={ENIGMA_METRICS}
      loading={timelines.isLoading}
      onAreaClick={handleAreaClick}
      onAxisClick={handleAxisClick}
      showAggregationSelector={true}
      defaultAggregationPeriod="monthly"
      showModeSelector={false}
      cacheKey={`${cityEscapeId}-${enigmaId}`}
      opts={{
        request: {
          skipEncoding: true,
          transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        },
        response: {
          transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        },
      }}
    />
  );
}

function useEnigmaTrendsOverTimeChartTimelines(
  cityEscapeId?: string | null,
  enigmaId?: string,
  filters?: MatchFilter,
  opts?: WorkerQueryOptions
) {
  return useQuery({
    queryKey: [
      "EnigmaTrendsOverTimeChartTimelines",
      cityEscapeId,
      enigmaId,
      JSON.stringify(filters),
      JSON.stringify(opts),
    ],
    queryFn: () =>
      getTimelines(cityEscapeId as string | undefined, enigmaId, filters, opts),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    enabled: cityEscapeId !== undefined,
  });
}

function getTimelines(
  cityEscapeId?: string,
  enigmaId?: string,
  filters?: MatchFilter,
  opts?: WorkerQueryOptions
) {
  const worker = new EnigmaTrendsOverTimeChartWorker();
  return webWorkerQuery(worker, {
    type: "TRANSFORM_TIMELINES",
    data: { cityEscapeId, enigmaId, filters },
    opts: {
      traceKey: `EnigmaTrendsOverTimeChartWorker-${Date.now()}`,
      ...opts,
      response: {
        transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        ...(opts?.response || {}),
      },
    },
  }).finally(() => {
    worker.terminate();
  });
}
